import { Trans } from '@lingui/react'
import { useSelector } from 'react-redux'

import { TENANT_BE } from 'shared/consts'
import { type IState } from 'types/redux/store'
import Notification, {
  NOTIFICATION_TYPES,
} from 'views/components/molecules/Notification'
import {
  getErrorMessageList,
  getErxRemovedMessageList,
  getMpRemovedMessageList,
  getOutOfStockMessageList,
  getVoucherMessageList,
} from 'views/components/organisms/Cart/helper/hasMessages'
import { useMaxDosageValues } from 'views/components/organisms/Cart/hooks/useMaxDosageValues'
import { NotificationCart } from 'views/components/organisms/Cart/NotificationCart'
import NotificationCollectiveOrderWarning from 'views/components/organisms/Cart/NotificationCollectiveOrderWarning'
import NotificationODSModal, {
  type ProviderDataType,
} from 'views/components/organisms/Cart/NotificationODSModal'
import { NotificationOrderDraft } from 'views/components/organisms/Cart/NotificationOrderDraft'
import {
  hasErxServiceUnavailableError,
  hasOrderDraftError,
  hasRedPointsReservationError,
} from 'views/components/organisms/Cart/NotificationOrderDraft/hasOrderDraftError'
import { NotificationOutOfStock } from 'views/components/organisms/Cart/NotificationOutOfStock'
import { NotificationVoucher } from 'views/components/organisms/Cart/NotificationVoucher'
import Tenant from 'views/components/utils/Tenant'

// Dynamic key for Notification Error provided via the API
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const notificationErrorKeyMarked = /*i18n*/ 'promotion.engine.general.error'

const NotificationBlock = ({
  hasODS,
  providerData,
}: {
  hasODS: boolean
  providerData: ProviderDataType
}) => {
  const cartMessages = useSelector((state: IState) => state.cart.data.messages)
  const showSuccess = useSelector((state: IState) => state.cart.showSuccess)

  const redPointError = useSelector((state: IState) => state.cart.redPointError)
  const error = useSelector((state: IState) => state.cart.error)

  const outOfStockMessageList = getOutOfStockMessageList(cartMessages)

  const voucherMessageList = getVoucherMessageList(cartMessages)

  const mpRemovedMessageList = getMpRemovedMessageList(cartMessages)

  const erxRemovedMessageList = getErxRemovedMessageList(cartMessages)

  const { isForMaxDosageLoggedInUser, hasOverdrafts, maxDosageDetails } =
    useMaxDosageValues()

  const hasStillAddableDefaultQtyAndstillAddableCollectiveOrderQtyEqual =
    maxDosageDetails?.some(
      detail =>
        detail.stillAddableDefaultQty === detail.stillAddableCollectiveOrderQty
    )
  return (
    <>
      {showSuccess && !isForMaxDosageLoggedInUser && (
        <NotificationCart type={NOTIFICATION_TYPES.SUCCESS}>
          <Trans id="cart.notification.maxDosage.cartInfoBox.updated.text" />
        </NotificationCart>
      )}
      {/* for this moment only for BE tenant hide maxDosage warning when personal max qty and maxdosage max qty are equal */}
      {hasOverdrafts &&
        !hasStillAddableDefaultQtyAndstillAddableCollectiveOrderQtyEqual && (
          <Tenant tenant={[TENANT_BE]}>
            {() => <NotificationCollectiveOrderWarning />}
          </Tenant>
        )}
      {hasOverdrafts && (
        <Tenant not tenant={[TENANT_BE]}>
          {() => <NotificationCollectiveOrderWarning />}
        </Tenant>
      )}
      {hasODS && <NotificationODSModal providerData={providerData} />}
      {hasODS && mpRemovedMessageList.length > 0 && (
        <NotificationCart
          /*@ts-ignore*/
          type={
            mpRemovedMessageList[0].type.toLowerCase() ||
            NOTIFICATION_TYPES.INFO
          }
        >
          <Trans id="cart.notification.ods.removed.mp.products" />
        </NotificationCart>
      )}
      {hasODS && erxRemovedMessageList.length > 0 && (
        <NotificationCart
          /*@ts-ignore*/
          type={
            erxRemovedMessageList[0].type.toLowerCase() ||
            NOTIFICATION_TYPES.INFO
          }
        >
          <Trans id="cart.notification.ods.removed.erx.products" />
        </NotificationCart>
      )}
      {outOfStockMessageList.length > 0 && (
        <NotificationOutOfStock messages={outOfStockMessageList} />
      )}
      {voucherMessageList.length !== 0 && (
        <NotificationVoucher messages={voucherMessageList} />
      )}
      {hasOrderDraftError(error) && <NotificationOrderDraft />}
      {redPointError && (
        <NotificationCart type={NOTIFICATION_TYPES.ERROR}>
          <Trans id="cart.error.redpoints.invalidAmount" />
        </NotificationCart>
      )}
      {hasRedPointsReservationError(error) && (
        <NotificationCart
          type={NOTIFICATION_TYPES.ERROR}
          dataQaId="redpoints-reservation-failed-message"
        >
          <Trans id="cart.notification.redpoints.reservation.failed" />
        </NotificationCart>
      )}
      {hasErxServiceUnavailableError(error) && (
        <NotificationCart type={NOTIFICATION_TYPES.INFO}>
          <Trans id="cart.notification.erx.unavailable" />
        </NotificationCart>
      )}
      {getErrorMessageList(cartMessages).map((curError, idx) => (
        <div className="l-content a-full-width u-padding-large--left u-padding-large--right">
          <Notification
            key={idx}
            label={<Trans id={curError.messages[0]} />}
            type={NOTIFICATION_TYPES.ERROR}
            className="u-margin--top"
          />
        </div>
      ))}
    </>
  )
}

export default NotificationBlock
